import React, { useRef, useState, useMemo } from 'react';
import { Image } from 'react-native';
import { SwitchTransition, Transition } from 'react-transition-group';
import { createStyleSheet } from '../../../../utils';
import { useStyleSheet, useThemeContext } from '../../../../hooks';
import { AUDIO_TYPES, VIDEO_TYPES, IMAGE_TYPES } from '../../../../constants';
import { Text } from '../../../Text';
import { Focusable } from '../../../Focusable';
import { Divider } from '../../../Divider';
import { Icon } from '../../../Icon';
import { IconButton } from '../../../IconButton';
import { View } from '../../../View';
const ANIMATION_DURATION = 250;
export const File = ({ style, id, data, name, type, createdAt, author, onPress, onDownload }) => {
    const styles = useStyleSheet(getStyles);
    const { size } = useThemeContext();
    const [isHovered, setIsHovered] = useState(false);
    const donwloadRef = useRef(null);
    const downloadSectionTransitionStyles = useMemo(() => {
        const offset = -1 - size.C * 2 - size.C * 2 - size.J - size.I;
        return {
            entering: {
                marginRight: -size.C,
                transform: [{ scale: 1 }],
                marginLeft: 0
            },
            entered: {
                marginRight: -size.C,
                transform: [{ scale: 1 }],
                marginLeft: 0
            },
            exiting: {
                marginRight: offset,
                transform: [{ scale: 0 }],
                marginLeft: size.I
            },
            exited: {
                marginRight: offset,
                transform: [{ scale: 0 }],
                marginLeft: size.I
            },
            unmounted: {}
        };
    }, [size]);
    const infoTransitionStyles = useMemo(() => ({
        entering: { transform: [{ translateY: 0 }] },
        entered: { transform: [{ translateY: 0 }] },
        exiting: { transform: [{ translateY: -size.H }] },
        exited: { transform: [{ translateY: size.H }] },
        unmounted: {}
    }), [size]);
    return (React.createElement(Focusable, { style: [styles.container, style], hoverStyle: styles.hover, onPress: () => {
            onPress({ id, data, name, type, createdAt, author }, 'self');
        }, onMouseEnter: () => {
            setIsHovered(true);
        }, onMouseLeave: () => {
            setIsHovered(false);
        } },
        type === 'application/pdf' ? (React.createElement(View, { style: [styles.preview, styles.preview__pdf] },
            React.createElement(Icon, { name: 'document-pdf', size: size.J, color: (color) => color.invert2.p26 }))) : AUDIO_TYPES.includes(type) ? (React.createElement(View, { style: [styles.preview, styles.preview__pdf] },
            React.createElement(Icon, { name: 'device-eq', size: size.J, color: (color) => color.invert2.p26 }))) : VIDEO_TYPES.includes(type) ? (React.createElement(View, { style: [styles.preview, styles.preview__pdf] },
            React.createElement(Icon, { name: 'video', size: size.J, color: (color) => color.invert2.p26 }))) : IMAGE_TYPES.includes(type) ? (React.createElement(Image, { source: { uri: data }, style: styles.preview })) : (React.createElement(View, { style: [styles.preview, styles.preview__document] },
            React.createElement(Icon, { name: 'document', size: size.J, color: (color) => color.invert2.p26 }))),
        React.createElement(View, { style: styles.main },
            React.createElement(Text, { type: 'body', size: 'md', weight: 'semibold', style: styles.name, numberOfLines: 1 }, name),
            React.createElement(View, { style: { height: size.H, overflow: 'hidden' } },
                React.createElement(SwitchTransition, { mode: 'out-in' },
                    React.createElement(Transition, { key: isHovered ? 'active' : 'idle', timeout: {
                            appear: 0,
                            enter: ANIMATION_DURATION / 2,
                            exit: ANIMATION_DURATION / 2
                        }, mountOnEnter: true, unmountOnExit: true }, (state) => {
                        var _a;
                        return (React.createElement(Text, { type: 'body', size: 'sm', weight: 'regular', style: [
                                styles.info,
                                Object.assign({}, infoTransitionStyles[state])
                            ], numberOfLines: 1 }, isHovered
                            ? 'Open in preview'
                            : (_a = type.split('/')[1]) === null || _a === void 0 ? void 0 : _a.toUpperCase()));
                    })))),
        React.createElement(Transition, { nodeRef: donwloadRef, in: isHovered, timeout: ANIMATION_DURATION / 2 }, (state) => (React.createElement(View, { ref: donwloadRef, style: [
                styles.download,
                Object.assign({}, downloadSectionTransitionStyles[state])
            ] },
            React.createElement(Divider, { style: styles.divider, type: 'vertical' }),
            React.createElement(IconButton, { type: 'tertiary', size: 'sm', accent: 'neutral', value: 'arrow-download', onPress: () => {
                    onDownload({ id, data, name, type, createdAt, author });
                } }),
            React.createElement(IconButton, { style: styles.openInNewTab, type: 'tertiary', size: 'sm', accent: 'neutral', value: 'open', onPress: () => {
                    onPress({ id, data, name, type, createdAt, author }, 'blank');
                } }))))));
};
const getStyles = createStyleSheet(({ color, size }) => ({
    container: {
        backgroundColor: color.invert2.p4,
        paddingVertical: size.C,
        paddingLeft: size.C,
        paddingRight: size.I,
        borderRadius: size.G,
        maxWidth: 275,
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden'
    },
    hover: {
        backgroundColor: color.invert2.p10
    },
    main: {
        flex: 1
    },
    name: {
        color: color.invert2.p100
    },
    info: {
        color: color.invert2.p56,
        transition: `transform ${ANIMATION_DURATION / 2}ms ease-out`
    },
    preview: {
        width: 49,
        height: 49,
        borderRadius: size.D,
        borderColor: color.invert2.p4,
        borderWidth: 1,
        marginRight: size.C
    },
    preview__pdf: {
        backgroundColor: color.bg.v1,
        padding: size.E
    },
    preview__document: {
        backgroundColor: color.bg.v1,
        padding: size.E
    },
    divider: {
        marginVertical: size.C,
        marginHorizontal: size.C
    },
    download: {
        flexDirection: 'row',
        alignItems: 'center',
        transition: `margin-right 250ms ease-out, transform ${ANIMATION_DURATION / 2}ms ease-out`,
        transform: [
            {
                scale: 0
            }
        ],
        marginRight: -1 - size.C * 2 - size.C * 2 - size.J - size.I,
        marginLeft: size.I
    },
    openInNewTab: {
        marginLeft: size.B
    }
}));
//# sourceMappingURL=File.web.js.map